import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTimes, FaInfoCircle } from 'react-icons/fa';
import { getCookiePreferences, saveCustomPreferences } from '../utils/cookieUtils';
import './CookieManager.css';

const CookieManager = ({ onClose }) => {
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Load existing preferences
    const savedPreferences = getCookiePreferences();
    setPreferences(savedPreferences);
  }, []);

  const handleToggle = (category) => {
    if (category === 'necessary') {
      return; // Cannot disable necessary cookies
    }
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSave = () => {
    saveCustomPreferences(preferences);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveCustomPreferences(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(allRejected);
    saveCustomPreferences(allRejected);
    onClose();
  };

  const cookieCategories = [
    {
      id: 'necessary',
      title: 'Necessary Cookies',
      description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility.',
      required: true
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
      required: false
    },
    {
      id: 'marketing',
      title: 'Marketing Cookies',
      description: 'These cookies are used to track visitors across websites to display relevant advertisements and measure marketing campaign effectiveness.',
      required: false
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.',
      required: false
    }
  ];

  return (
    <div className="cookie-manager-overlay" onClick={onClose}>
      <div className="cookie-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cookie-manager-header">
          <h2 className="cookie-manager-title">Cookie Preferences</h2>
          <button className="cookie-manager-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="cookie-manager-content">
          <p className="cookie-manager-intro">
            Manage your cookie preferences. You can enable or disable different types of cookies below. 
            Note that necessary cookies cannot be disabled as they are essential for the website to function.
          </p>

          <div className="cookie-categories">
            {cookieCategories.map((category) => (
              <div key={category.id} className="cookie-category">
                <div className="cookie-category-header">
                  <div className="cookie-category-info">
                    <h3 className="cookie-category-title">
                      {category.title}
                      {category.required && (
                        <span className="cookie-required-badge">Required</span>
                      )}
                    </h3>
                    <p className="cookie-category-description">{category.description}</p>
                  </div>
                  <label className="cookie-toggle">
                    <input
                      type="checkbox"
                      checked={preferences[category.id]}
                      onChange={() => handleToggle(category.id)}
                      disabled={category.required}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="cookie-manager-info">
            <FaInfoCircle className="cookie-info-icon" />
            <p className="cookie-manager-info-text">
              For more detailed information about how we use cookies, please read our{' '}
              <Link to="/cookie-policy" className="cookie-manager-link" onClick={onClose}>
                Cookie Policy
              </Link>.
            </p>
          </div>
        </div>

        <div className="cookie-manager-footer">
          <button className="cookie-manager-btn cookie-manager-btn-secondary" onClick={handleRejectAll}>
            Reject All
          </button>
          <button className="cookie-manager-btn cookie-manager-btn-secondary" onClick={handleAcceptAll}>
            Accept All
          </button>
          <button className="cookie-manager-btn cookie-manager-btn-primary" onClick={handleSave}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieManager;

